import React, { useState } from "react";
import { FaFileCsv, FaFileExcel } from "react-icons/fa";
import { RevenueNode } from "./useAssetRevenue";

// interface Props {
//   title?: string;
//   data: RevenueNode[];
//   view: "DAY" | "MTD" | "YTD";
// }
interface Props {
  title?: string;

  data: RevenueNode[];

  view: "DAY" | "MTD" | "YTD";

  breakdownView:
    | "TOTAL"
    | "PEAK"
    | "OFF_PEAK"
    | "ALL";

  onBreakdownChange: (
    val:
      | "TOTAL"
      | "PEAK"
      | "OFF_PEAK"
      | "ALL"
  ) => void;
}

// const RevenueSummaryTable: React.FC<Props> = ({
//   title,
//   data,
//   view,
// }) => {
  const RevenueSummaryTable: React.FC<Props> = ({
  title,
  data,
  view,
  breakdownView,
  onBreakdownChange,
}) =>{
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const [expandAll, setExpandAll] = useState(false);

  const buildExpandMap = (
    nodes: RevenueNode[],
    parent = ""
  ): Record<string, boolean> => {
    let map: Record<string, boolean> = {};

    nodes.forEach((node) => {
      const key = parent + node.label;

      if (node.children?.length) {
        map[key] = true;

        Object.assign(
          map,
          buildExpandMap(node.children, key)
        );
      }
    });

    return map;
  };

  const handleExpandAll = () => {
    if (!expandAll) {
      setExpanded(buildExpandMap(data));
    } else {
      setExpanded({});
    }

    setExpandAll(!expandAll);
  };

  const toggle = (key: string) => {
    setExpanded((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const getViewData = (node: RevenueNode) => {
    if (view === "DAY") return node.yesterday;
    if (view === "MTD") return node.month_to_date;

    return node.year_to_date;
  };

  const formatValue = (value?: number) => {
    if (value === null || value === undefined || value === 0) {
      return <span className="text-gray-400">-</span>;
    }

    return value.toLocaleString(undefined, {
      maximumFractionDigits: 2,
    });
  };

  const renderRow = (
    node: RevenueNode,
    level = 0,
    parent = ""
  ): any => {
    const key = parent + node.label;

    const hasChildren = node.children?.length;

    const values = getViewData(node);
    const renderBreakdownRow = (
  type: "TOTAL" | "PEAK" | "OFF_PEAK",
  label: string
) => (
  <tr className="hover:bg-gray-50">
 <td
  style={{
    paddingLeft: `${12 + level * 20}px`,
  }}
  className="p-3 cursor-pointer font-medium"
  onClick={() =>
    hasChildren && toggle(key)
  }
>
  {(breakdownView !== "ALL" ||
    type === "TOTAL") &&
  hasChildren ? (
    <span className="mr-2 text-xs">
      {expanded[key]
        ? "▼"
        : "▶"}
    </span>
  ) : null}

  {breakdownView === "ALL"
    ? type === "TOTAL"
      ? node.label
      : ""
    : node.label}
</td>

    <td className="p-3">
      {label}
    </td>

    <td className="text-right">
      {formatValue(
        node.yesterday?.[type]
      )}
    </td>

    <td className="text-right">
      {formatValue(
        node.month_to_date?.[type]
      )}
    </td>

    <td className="text-right">
      {formatValue(
        node.year_to_date?.[type]
      )}
    </td>
  </tr>
);

   return (
  <React.Fragment key={key}>
    {breakdownView === "ALL" ? (
      <>
        {renderBreakdownRow(
          "TOTAL",
          "Total"
        )}

        {renderBreakdownRow(
          "PEAK",
          "Peak"
        )}

        {renderBreakdownRow(
          "OFF_PEAK",
          "Off Peak"
        )}
      </>
    ) : (
      renderBreakdownRow(
        breakdownView,
        breakdownView ===
          "OFF_PEAK"
          ? "Off Peak"
          : breakdownView ===
            "PEAK"
          ? "Peak"
          : "Total"
      )
    )}

    {node.children &&
      node.children.length >
        0 &&
      expanded[key] &&
      node.children.map(
        (child) =>
          renderRow(
            child,
            level + 1,
            key
          )
      )}
  </React.Fragment>
);
  };
  const totalRow = data.reduce(
  (acc, node) => ({
    yesterday: {
      TOTAL:
        (acc.yesterday?.TOTAL || 0) +
        (node.yesterday?.TOTAL || 0),
      PEAK:
        (acc.yesterday?.PEAK || 0) +
        (node.yesterday?.PEAK || 0),
      OFF_PEAK:
        (acc.yesterday?.OFF_PEAK || 0) +
        (node.yesterday?.OFF_PEAK || 0),
    },

    month_to_date: {
      TOTAL:
        (acc.month_to_date?.TOTAL || 0) +
        (node.month_to_date?.TOTAL || 0),
      PEAK:
        (acc.month_to_date?.PEAK || 0) +
        (node.month_to_date?.PEAK || 0),
      OFF_PEAK:
        (acc.month_to_date?.OFF_PEAK || 0) +
        (node.month_to_date?.OFF_PEAK || 0),
    },

    year_to_date: {
      TOTAL:
        (acc.year_to_date?.TOTAL || 0) +
        (node.year_to_date?.TOTAL || 0),
      PEAK:
        (acc.year_to_date?.PEAK || 0) +
        (node.year_to_date?.PEAK || 0),
      OFF_PEAK:
        (acc.year_to_date?.OFF_PEAK || 0) +
        (node.year_to_date?.OFF_PEAK || 0),
    },
  }),
  {
    yesterday: { TOTAL: 0, PEAK: 0, OFF_PEAK: 0 },
    month_to_date: { TOTAL: 0, PEAK: 0, OFF_PEAK: 0 },
    year_to_date: { TOTAL: 0, PEAK: 0, OFF_PEAK: 0 },
  }
);

  return (
    <div className="bg-white rounded-xl shadow overflow-hidden">
      <div className="flex justify-between items-center p-4 bg-gray-50">
        <h2 className="text-lg text-gray-700">
          {title}
        </h2>

        <div className="flex gap-4">
          <button className="flex items-center gap-2 text-sm text-gray-600">
            <FaFileCsv /> CSV
          </button>

          <button className="flex items-center gap-2 text-sm text-gray-600">
            <FaFileExcel /> Excel
          </button>
        </div>
      </div>

      {/* <div className="p-3"> */}
      <div className="p-3 flex gap-3 items-center flex-wrap">
        {/* <button
          onClick={handleExpandAll}
          className="px-3 py-1 rounded text-sm bg-gray-200 text-gray-600"
        >
          {expandAll ? "Collapse All" : "Expand All"}
        </button> */}
        <button
  onClick={() =>
    onBreakdownChange("TOTAL")
  }
  className={`px-4 py-2 rounded-md text-sm font-semibold ${
    breakdownView === "TOTAL"
      ? "bg-[#6C63FF] text-white"
      : "bg-gray-200 text-gray-600"
  }`}
>
  TOTAL
</button>

<button
  onClick={() =>
    onBreakdownChange("ALL")
  }
  className={`px-4 py-2 rounded-md text-sm font-semibold ${
    breakdownView === "ALL"
      ? "bg-[#6C63FF] text-white"
      : "bg-gray-200 text-gray-600"
  }`}
>
  ALL
</button>

<button
  onClick={() =>
    onBreakdownChange("PEAK")
  }
  className={`px-4 py-2 rounded-md text-sm font-semibold ${
    breakdownView === "PEAK"
      ? "bg-[#6C63FF] text-white"
      : "bg-gray-200 text-gray-600"
  }`}
>
  PEAK
</button>

<button
  onClick={() =>
    onBreakdownChange("OFF_PEAK")
  }
  className={`px-4 py-2 rounded-md text-sm font-semibold ${
    breakdownView === "OFF_PEAK"
      ? "bg-[#6C63FF] text-white"
      : "bg-gray-200 text-gray-600"
  }`}
>
  OFFPEAK
</button>
      </div>

      <div className="overflow-auto max-h-[700px]">
        <table className="w-full text-sm">
          {/* <thead className="sticky top-0 bg-[#ECEBFF]">
            <tr>
              <th className="p-3 text-left">Metric</th>
              <th className="p-3 text-right">Peak</th>
              <th className="p-3 text-right">Off Peak</th>
              <th className="p-3 text-right">Total</th>
            </tr>
          </thead> */}
        <thead className="sticky top-0 bg-[#ECEBFF]">
  <tr>
    <th className="p-3 text-left">
      Metric
    </th>

    <th className="p-3 text-left">
      Time Of Views
    </th>

    <th className="p-3 text-right">
      Day
    </th>

    <th className="p-3 text-right">
      MTD
    </th>

    <th className="p-3 text-right">
      YTD
    </th>
  </tr>
</thead>

          {/* <tbody>
            {data.map((node) => renderRow(node))}
          </tbody> */}
          <tbody>

  <tr className="font-semibold bg-gray-50">
    <td className="p-3">Total</td>

    <td className="p-3">
      {breakdownView === "OFF_PEAK"
        ? "Off Peak"
        : breakdownView === "PEAK"
        ? "Peak"
        : "Total"}
    </td>

    <td className="text-right">
      {formatValue(
        breakdownView === "PEAK"
          ? totalRow.yesterday.PEAK
          : breakdownView === "OFF_PEAK"
          ? totalRow.yesterday.OFF_PEAK
          : totalRow.yesterday.TOTAL
      )}
    </td>

    <td className="text-right">
      {formatValue(
        breakdownView === "PEAK"
          ? totalRow.month_to_date.PEAK
          : breakdownView === "OFF_PEAK"
          ? totalRow.month_to_date.OFF_PEAK
          : totalRow.month_to_date.TOTAL
      )}
    </td>

    <td className="text-right">
      {formatValue(
        breakdownView === "PEAK"
          ? totalRow.year_to_date.PEAK
          : breakdownView === "OFF_PEAK"
          ? totalRow.year_to_date.OFF_PEAK
          : totalRow.year_to_date.TOTAL
      )}
    </td>
  </tr>

  {data.map((node) => renderRow(node))}
</tbody>
        </table>
      </div>
    </div>
  );
};

export default RevenueSummaryTable;